import { Event } from './types';

type EventDateTime = Pick<Event, 'date' | 'time' | 'createdAt'>;

/**
 * Compares two events by date and time for sorting.
 *
 * Sorting logic:
 * 1. First by date (earliest first)
 * 2. For same date: events with times come before events without times
 * 3. Events with times are sorted by start time (earliest first)
 * 4. Events without times maintain original order
 *
 * Sorting output would be:
 * - Event 1 - date 18.06.2025 and time 8:00-9:00
 * - Event 2 - date 18.06.2025 and time 10:00-11:00
 * - Event 3 - only date 18.06.2025
 *
 */
export const compareEventsDateTime = (a: EventDateTime, b: EventDateTime): number => {
  const dateA = new Date(a.date);
  const dateB = new Date(b.date);

  if (dateA.getTime() !== dateB.getTime()) {
    return dateA.getTime() - dateB.getTime();
  }

  // Check if both events have times
  const hasTimeA = a.time?.startTime;
  const hasTimeB = b.time?.startTime;

  // If one has time and the other doesn't, prioritize the one with time
  if (hasTimeA && !hasTimeB) return -1;
  if (!hasTimeA && hasTimeB) return 1;

  // If both have times, compare them
  if (hasTimeA && hasTimeB) {
    return hasTimeA.localeCompare(hasTimeB);
  }

  // If neither has time, maintain original order
  return 0;
};

export const compareEventsCreatedAt = (a: EventDateTime, b: EventDateTime): number => {
  if (!a.createdAt || !b.createdAt) return 0;
  if (typeof a.createdAt?.toDate !== 'function' || typeof b.createdAt?.toDate !== 'function')
    return 0;

  return a.createdAt?.toDate().getTime() - b.createdAt?.toDate().getTime();
};

/**
 * Can be used for sorting events that have date, time and createdAt properties
 * It will not work for events straight from the database, as the main event doesn't have a date.
 * @param events Array of events to sort
 * @returns Sorted array of events, first by createdAt timestamp then by date/time
 */

export const sortEvents = <T extends EventDateTime>(events: T[]): T[] =>
  [...events].sort(compareEventsCreatedAt).sort(compareEventsDateTime);
